import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Stack
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { getCarePlan, getAllCarePlans, setCarePlan } from '../../services/careCoordinationService';

const statusOptions = ['IN_PROGRESS', 'COMPLETED', 'ON_HOLD', 'CANCELLED'];

export default function CarePlanTab({ patientId, patients }) {
  const [carePlans, setCarePlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadCarePlans = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = patientId 
        ? await getCarePlan(patientId)
        : await getAllCarePlans();
      
      setCarePlans(data ? (Array.isArray(data) ? data : [data]) : []);
    } catch (err) {
      setError('Failed to load care plans');
      console.error('Error loading care plans:', err);
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    loadCarePlans();
  }, [patientId, loadCarePlans]);

  // Helper to get patient name by id
  const getPatientName = (id) => {
    console.log('CarePlanTab: getPatientName called with:', {
      patientId: id,
      patientsAvailable: patients?.length || 0,
      patients: patients
    });
    
    if (!id || !patients || patients.length === 0) return 'Unknown Patient';
    
    // Try different ways to find the patient
    let p = patients.find(p => p.patientId === id);
    if (!p) p = patients.find(p => p.id === id);
    if (!p) p = patients.find(p => p._id === id);
    
    console.log('CarePlanTab: Patient lookup result:', {
      patientId: id,
      foundPatient: p,
      lookupMethods: {
        byPatientId: patients.find(p => p.patientId === id),
        byId: patients.find(p => p.id === id),
        by_id: patients.find(p => p._id === id)
      }
    });
    
    if (p) {
      // Handle different patient name structures
      if (p.firstName && p.lastName) {
        return `${p.firstName} ${p.lastName}`;
      } else if (p.name) {
        return p.name;
      } else if (p.firstName) {
        return p.firstName;
      } else if (p.lastName) {
        return p.lastName;
      }
    }
    
    // If we can't find the patient, show a more user-friendly message
    return `Patient ${id.substring(0, 8)}...`;
  };

  const handleAdd = () => {
    setSelectedPlan({
      patientId: patientId || '',
      title: '',
      description: '',
      goals: [{
        title: '',
        target: '',
        status: 'IN_PROGRESS',
        interventions: [''],
        notes: ''
      }]
    });
    setIsDialogOpen(true);
  };

  const handleEdit = (plan) => {
    setSelectedPlan({ ...plan });
    setIsDialogOpen(true);
  };

  const handleDelete = async (plan) => {
    if (!window.confirm('Are you sure you want to delete this care plan?')) return;
    
    try {
      await setCarePlan(plan.patientId, plan.title, plan.description, []);
      await loadCarePlans();
    } catch (err) {
      setError('Failed to delete care plan');
      console.error('Error deleting care plan:', err);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError('');
      await setCarePlan(
        selectedPlan.patientId,
        selectedPlan.title,
        selectedPlan.description,
        selectedPlan.goals
      );
      setIsDialogOpen(false);
      await loadCarePlans();
    } catch (err) {
      setError('Failed to save care plan');
      console.error('Error saving care plan:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddGoal = () => {
    setSelectedPlan({
      ...selectedPlan,
      goals: [
        ...selectedPlan.goals,
        {
          title: '',
          target: '',
          status: 'IN_PROGRESS',
          interventions: [''],
          notes: ''
        }
      ]
    });
  };

  const handleAddIntervention = (goalIndex) => {
    const updatedGoals = [...selectedPlan.goals];
    updatedGoals[goalIndex].interventions.push('');
    setSelectedPlan({
      ...selectedPlan,
      goals: updatedGoals
    });
  };

  const handleGoalChange = (index, field, value) => {
    const updatedGoals = [...selectedPlan.goals];
    updatedGoals[index] = {
      ...updatedGoals[index],
      [field]: value
    };
    setSelectedPlan({
      ...selectedPlan,
      goals: updatedGoals
    });
  };

  const handleInterventionChange = (goalIndex, interventionIndex, value) => {
    const updatedGoals = [...selectedPlan.goals];
    updatedGoals[goalIndex].interventions[interventionIndex] = value;
    setSelectedPlan({
      ...selectedPlan,
      goals: updatedGoals
    });
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <h2>Care Plans</h2>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAdd}
        >
          Add Care Plan
        </Button>
      </Stack>

      {error && (
        <Box sx={{ color: 'error.main', mb: 2 }}>{error}</Box>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Patient</TableCell>
              <TableCell>Goal</TableCell>
              <TableCell>Intervention</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {carePlans.map((plan) => (
              plan.goals?.map((goal, idx) => (
                goal.interventions?.map((intervention, intIdx) => (
                  <TableRow key={`${plan.id}-${idx}-${intIdx}`}>
                    <TableCell>{getPatientName(plan.patientId)}</TableCell>
                    <TableCell>{goal.title}</TableCell>
                    <TableCell>{intervention}</TableCell>
                    <TableCell>{goal.status}</TableCell>
                    <TableCell>{goal.notes}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton onClick={() => handleEdit(plan)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton onClick={() => handleDelete(plan)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ))
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog 
        open={isDialogOpen} 
        onClose={() => setIsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedPlan?.id ? 'Edit Care Plan' : 'New Care Plan'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {!patientId && (
              <TextField
                fullWidth
                label="Patient"
                select
                value={selectedPlan?.patientId || ''}
                onChange={(e) => setSelectedPlan({
                  ...selectedPlan,
                  patientId: e.target.value
                })}
                sx={{ mb: 2 }}
              >
                {patients?.map((patient) => (
                  <MenuItem key={patient.id} value={patient.id}>
                    {`${patient.firstName} ${patient.lastName}`}
                  </MenuItem>
                ))}
              </TextField>
            )}

            <TextField
              fullWidth
              label="Title"
              value={selectedPlan?.title || ''}
              onChange={(e) => setSelectedPlan({
                ...selectedPlan,
                title: e.target.value
              })}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Description"
              multiline
              rows={2}
              value={selectedPlan?.description || ''}
              onChange={(e) => setSelectedPlan({
                ...selectedPlan,
                description: e.target.value
              })}
              sx={{ mb: 2 }}
            />

            {selectedPlan?.goals?.map((goal, goalIndex) => (
              <Box key={goalIndex} sx={{ mb: 3, p: 2, border: '1px solid #eee', borderRadius: 1 }}>
                <TextField
                  fullWidth
                  label="Goal Title"
                  value={goal.title}
                  onChange={(e) => handleGoalChange(goalIndex, 'title', e.target.value)}
                  sx={{ mb: 2 }}
                />

                <TextField
                  fullWidth
                  label="Target"
                  value={goal.target}
                  onChange={(e) => handleGoalChange(goalIndex, 'target', e.target.value)}
                  sx={{ mb: 2 }}
                />

                <TextField
                  select
                  fullWidth
                  label="Status"
                  value={goal.status}
                  onChange={(e) => handleGoalChange(goalIndex, 'status', e.target.value)}
                  sx={{ mb: 2 }}
                >
                  {statusOptions.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status.replace('_', ' ')}
                    </MenuItem>
                  ))}
                </TextField>

                {goal.interventions.map((intervention, intIndex) => (
                  <TextField
                    key={intIndex}
                    fullWidth
                    label={`Intervention ${intIndex + 1}`}
                    value={intervention}
                    onChange={(e) => handleInterventionChange(goalIndex, intIndex, e.target.value)}
                    sx={{ mb: 1 }}
                  />
                ))}

                <Button
                  variant="outlined"
                  onClick={() => handleAddIntervention(goalIndex)}
                  sx={{ mb: 2 }}
                >
                  Add Intervention
                </Button>

                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={2}
                  value={goal.notes}
                  onChange={(e) => handleGoalChange(goalIndex, 'notes', e.target.value)}
                />
              </Box>
            ))}

            <Button
              variant="outlined"
              onClick={handleAddGoal}
              sx={{ mt: 1 }}
            >
              Add Goal
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            disabled={loading}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 